import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { BsCalendar2WeekFill } from 'react-icons/bs';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

import { userAppData } from '../MainApp.js';
import { BookingQuestionsModal } from '../common/modals.js';
import Header from '../common/header.js';
import { getRotasBootstrap, saveRota, searchRotaStaff } from './sysManagerRotasApi.js';
import SysManagerRotasTimetableGrid from './SysManagerRotasTimetableGrid.js';

import './SysManagerRotasDesktop.css';
import './SysManagerRotasMobile.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const DEFAULT_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const DAY_OPTIONS = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];
const STATUS_OPTIONS = ['ACTIVE', 'DRAFT', 'INACTIVE', 'ARCHIVED'];
const CREATE_STAGE_OPTIONS = [
  { id: 'DETAILS', label: 'Rota details' },
  { id: 'CYCLE', label: 'Rota cycle' },
  { id: 'BLOCKS', label: 'Rota blocks' },
];
const DAY_LABELS = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};
const ASSIGNMENT_MODE_OPTIONS = [
  { value: 'USER', label: 'Named person' },
  { value: 'TEXT', label: 'Text' },
  { value: 'NONE', label: 'None' },
];

function buildClientBlockId(prefix, index) {
  return `${prefix}-${index}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function createAssignmentState(sourceBlock = {}, blockIndex = 0, prefix = 'block') {
  return {
    clientBlockId: sourceBlock.clientBlockId || buildClientBlockId(prefix, blockIndex),
    blockId: String(sourceBlock.blockId || '').trim(),
    slotGroupId: String(sourceBlock.slotGroupId || '').trim(),
    dayOfWeek: sourceBlock.dayOfWeek || '',
    period: sourceBlock.period || '',
    start: sourceBlock.start || '',
    end: sourceBlock.end || '',
    assignmentType: sourceBlock.assignmentType || 'USER',
    selectedUserOption: sourceBlock.selectedUserOption || null,
    textValue: sourceBlock.textValue || '',
    notes: sourceBlock.notes || '',
  };
}

function createInitialFormState() {
  return {
    editMode: false,
    masterRotaId: '',
    storedRotaId: '',
    rotaName: '',
    description: '',
    status: 'ACTIVE',
    availableOnDashboard: false,
    creationMode: 'EVERY_WEEK',
    rotaType: 'LESSON_BASED',
    timingSetId: '',
    appliesTo: {
      weekMode: 'ALL_WEEKS',
      weekNumbersText: '',
      daysOfWeek: [...DEFAULT_DAYS],
      dateFrom: '',
      dateTo: '',
    },
    blocks: [],
    weekVariants: [],
  };
}

function createWeekVariant(weekNumber, weekLabel, blocks = [], prefix = 'week') {
  return {
    rotaId: blocks[0]?.rotaId || '',
    weekNumber,
    weekLabel,
    blocks: blocks.map((block, index) => createAssignmentState(block, index, `${prefix}-${weekNumber}`)),
  };
}

function mapBlocksForLoadedRota(blocks = [], prefix = 'loaded') {
  return (Array.isArray(blocks) ? blocks : []).map((block, index) => createAssignmentState({
    blockId: block.blockId || '',
    slotGroupId: block.slotGroupId || '',
    dayOfWeek: block.dayOfWeek || '',
    period: block.period || '',
    start: block.start || '',
    end: block.end || '',
    assignmentType: block.assignmentType || 'TEXT',
    selectedUserOption: block.assignmentType === 'USER' && block.userId
      ? {
        value: block.userId,
        label: block.displayName || block.userId,
      }
      : null,
    textValue: block.assignmentType === 'TEXT' ? String(block.text || '').trim() : '',
    notes: block.notes || '',
  }, index, prefix));
}

function mapRotaDetailToFormState(rotaDetail = {}) {
  const appliesTo = rotaDetail.appliesTo || {};
  return {
    editMode: true,
    masterRotaId: String(rotaDetail.masterRotaId || '').trim(),
    storedRotaId: String(rotaDetail.storedRotaId || '').trim(),
    rotaName: String(rotaDetail.rotaName || '').trim(),
    description: String(rotaDetail.description || '').trim(),
    status: String(rotaDetail.status || 'ACTIVE').trim().toUpperCase(),
    availableOnDashboard: Boolean(rotaDetail.availableOnDashboard),
    creationMode: String(rotaDetail.creationMode || 'EVERY_WEEK').trim().toUpperCase(),
    rotaType: String(rotaDetail.rotaType || 'LESSON_BASED').trim().toUpperCase(),
    timingSetId: String(rotaDetail.timingSetId || '').trim(),
    appliesTo: {
      weekMode: String(appliesTo.weekMode || 'ALL_WEEKS').trim().toUpperCase(),
      weekNumbersText: Array.isArray(appliesTo.weekNumbers) ? appliesTo.weekNumbers.join(', ') : '',
      daysOfWeek: Array.isArray(appliesTo.daysOfWeek) && appliesTo.daysOfWeek.length > 0 ? appliesTo.daysOfWeek : [...DEFAULT_DAYS],
      dateFrom: String(appliesTo.dateFrom || '').trim(),
      dateTo: String(appliesTo.dateTo || '').trim(),
    },
    blocks: mapBlocksForLoadedRota(rotaDetail.blocks, 'loaded-block'),
    weekVariants: (Array.isArray(rotaDetail.weekVariants) ? rotaDetail.weekVariants : []).map((variant) => ({
      rotaId: String(variant.rotaId || '').trim(),
      weekNumber: variant.weekNumber,
      weekLabel: variant.weekLabel,
      blocks: mapBlocksForLoadedRota(variant.blocks, `loaded-week-${variant.weekNumber}`),
    })),
  };
}

function buildBlockMatchKey(block = {}) {
  return [
    String(block.dayOfWeek || '').trim().toUpperCase(),
    String(block.period || '').trim(),
    String(block.start || '').trim(),
    String(block.end || '').trim(),
  ].join('|');
}

function getAssignmentModeForBlock(block = {}) {
  if (block.assignmentType === 'USER') {
    return 'USER';
  }

  if (block.assignmentType === 'TEXT' && String(block.textValue || '').trim().toUpperCase() === 'NONE') {
    return 'NONE';
  }

  return 'TEXT';
}

function timeStringToMinutes(value) {
  const parts = String(value || '').split(':');
  if (parts.length !== 2) {
    return Number.NaN;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return Number.NaN;
  }

  return (hours * 60) + minutes;
}

function validateBlocks(blocks, rotaType, selectedDays = []) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return 'Add at least one rota block before saving.';
  }

  const orderedBlocks = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    const period = String(block?.period || '').trim();
    const start = String(block?.start || '').trim();
    const end = String(block?.end || '').trim();
    const assignmentType = String(block?.assignmentType || '').trim().toUpperCase();
    const dayOfWeek = String(block?.dayOfWeek || '').trim().toUpperCase();

    if (!period) {
      return `Block ${index + 1} needs a name.`;
    }

    if (rotaType === 'CUSTOM' && !DAY_LABELS[dayOfWeek]) {
      return `Block ${index + 1} needs a day selected.`;
    }
    if (rotaType === 'CUSTOM' && !selectedDays.includes(dayOfWeek)) {
      return `Block ${index + 1} uses ${DAY_LABELS[dayOfWeek] || dayOfWeek}, which is not selected in the rota cycle.`;
    }

    const startMinutes = timeStringToMinutes(start);
    const endMinutes = timeStringToMinutes(end);
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) {
      return `Block ${index + 1} must have valid start and end times.`;
    }
    if (endMinutes <= startMinutes) {
      return `Block ${index + 1} must end after it starts.`;
    }

    if (assignmentType === 'USER') {
      if (!block?.selectedUserOption?.value || !block?.selectedUserOption?.label) {
        return `Block ${index + 1} needs a staff member selected.`;
      }
    }

    if (assignmentType === 'TEXT' && !String(block?.textValue || '').trim()) {
      return `Block ${index + 1} needs a text value.`;
    }

    orderedBlocks.push({
      ...block,
      period,
      start,
      end,
      assignmentType,
      dayOfWeek,
      startMinutes,
      endMinutes,
    });
  }

  if (rotaType === 'CUSTOM') {
    const overlapCheckBlocks = [...orderedBlocks].sort((firstBlock, secondBlock) => {
      if (firstBlock.dayOfWeek !== secondBlock.dayOfWeek) {
        return firstBlock.dayOfWeek.localeCompare(secondBlock.dayOfWeek);
      }
      const periodComparison = firstBlock.period.localeCompare(secondBlock.period, undefined, { sensitivity: 'base' });
      if (periodComparison !== 0) {
        return periodComparison;
      }
      if (firstBlock.startMinutes !== secondBlock.startMinutes) {
        return firstBlock.startMinutes - secondBlock.startMinutes;
      }
      return firstBlock.endMinutes - secondBlock.endMinutes;
    });

    for (let index = 1; index < overlapCheckBlocks.length; index += 1) {
      const previousBlock = overlapCheckBlocks[index - 1];
      const currentBlock = overlapCheckBlocks[index];
      const sameDay = currentBlock.dayOfWeek === previousBlock.dayOfWeek;
      const sameZone = currentBlock.period.localeCompare(previousBlock.period, undefined, { sensitivity: 'base' }) === 0;
      if (sameDay && sameZone && currentBlock.startMinutes < previousBlock.endMinutes) {
        return `Custom rota zone "${currentBlock.period}" has overlapping times on ${DAY_LABELS[currentBlock.dayOfWeek]}.`;
      }
    }
  }

  return '';
}

function buildPayload(formState) {
  const weekMode = String(formState.appliesTo.weekMode || 'ALL_WEEKS').trim().toUpperCase();
  const rawWeekNumbers = String(formState.appliesTo.weekNumbersText || '').trim();
  const parsedWeekNumbers = rawWeekNumbers
    ? rawWeekNumbers
      .split(',')
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isInteger(value) && value > 0)
    : [];

  return {
    mode: formState.editMode ? 'UPDATE' : 'CREATE',
    masterRotaId: String(formState.masterRotaId || '').trim() || null,
    storedRotaId: String(formState.storedRotaId || '').trim() || null,
    rotaName: String(formState.rotaName || '').trim(),
    module: 'CUSTOM',
    description: String(formState.description || '').trim(),
    status: String(formState.status || 'ACTIVE').trim().toUpperCase(),
    availableOnDashboard: Boolean(formState.availableOnDashboard),
    creationMode: String(formState.creationMode || 'EVERY_WEEK').trim().toUpperCase(),
    rotaType: String(formState.rotaType || 'LESSON_BASED').trim().toUpperCase(),
    timingSetId: formState.rotaType === 'LESSON_BASED'
      ? String(formState.timingSetId || '').trim()
      : null,
    appliesTo: {
      weekMode,
      weekNumbers: weekMode === 'SPECIFIC_WEEKS' ? parsedWeekNumbers : [],
      daysOfWeek: formState.appliesTo.daysOfWeek,
      dateFrom: String(formState.appliesTo.dateFrom || '').trim() || null,
      dateTo: String(formState.appliesTo.dateTo || '').trim() || null,
    },
    blocks: formState.blocks.map((block) => ({
      blockId: String(block.blockId || '').trim() || null,
      slotGroupId: String(block.slotGroupId || '').trim() || null,
      dayOfWeek: String(block.dayOfWeek || '').trim().toUpperCase() || null,
      period: String(block.period || '').trim(),
      start: String(block.start || '').trim(),
      end: String(block.end || '').trim(),
      assignmentType: String(block.assignmentType || '').trim().toUpperCase(),
      userId: block.assignmentType === 'USER' ? String(block?.selectedUserOption?.value || '').trim() : null,
      text: block.assignmentType === 'TEXT' ? String(block?.textValue || '').trim() : null,
      notes: String(block.notes || '').trim(),
    })),
    weekVariants: (Array.isArray(formState.weekVariants) ? formState.weekVariants : []).map((variant) => ({
      rotaId: String(variant.rotaId || '').trim() || null,
      weekNumber: variant.weekNumber,
      weekLabel: variant.weekLabel,
      blocks: (Array.isArray(variant.blocks) ? variant.blocks : []).map((block) => ({
        blockId: String(block.blockId || '').trim() || null,
        slotGroupId: String(block.slotGroupId || '').trim() || null,
        dayOfWeek: String(block.dayOfWeek || '').trim().toUpperCase() || null,
        period: String(block.period || '').trim(),
        start: String(block.start || '').trim(),
        end: String(block.end || '').trim(),
        assignmentType: String(block.assignmentType || '').trim().toUpperCase(),
        userId: block.assignmentType === 'USER' ? String(block?.selectedUserOption?.value || '').trim() : null,
        text: block.assignmentType === 'TEXT' ? String(block?.textValue || '').trim() : null,
        notes: String(block.notes || '').trim(),
      })),
    })),
  };
}

function formatAppliesToSummary(appliesTo = {}) {
  const daysOfWeek = Array.isArray(appliesTo.daysOfWeek) ? appliesTo.daysOfWeek : [];
  const weekMode = String(appliesTo.weekMode || 'ALL_WEEKS');
  const weekNumbers = Array.isArray(appliesTo.weekNumbers) ? appliesTo.weekNumbers : [];
  const dateFrom = appliesTo.dateFrom || '';
  const dateTo = appliesTo.dateTo || '';

  const parts = [];
  if (daysOfWeek.length > 0) {
    parts.push(daysOfWeek.join(', '));
  }
  if (weekMode === 'SPECIFIC_WEEKS' && weekNumbers.length > 0) {
    parts.push(`Weeks ${weekNumbers.join(', ')}`);
  } else {
    parts.push('All weeks');
  }
  if (dateFrom || dateTo) {
    parts.push(`${dateFrom || 'Any start'} to ${dateTo || 'Any end'}`);
  }

  return parts.join(' | ');
}

function mapTimingSetBlocks(timingSetId, timingSets, daysOfWeek = [], existingBlocks = [], prefix = 'timing') {
  const selectedTimingSet = timingSets.find((timingSet) => timingSet.timingSetId === timingSetId);
  if (!selectedTimingSet) {
    return [];
  }

  const existingBlockMap = new Map(
    (Array.isArray(existingBlocks) ? existingBlocks : []).map((block) => [buildBlockMatchKey(block), block]),
  );

  const normalisedDays = Array.isArray(daysOfWeek)
    ? daysOfWeek.map((dayCode) => String(dayCode || '').trim().toUpperCase()).filter(Boolean)
    : [];

  const mappedBlocks = [];

  normalisedDays.forEach((dayCode) => {
    selectedTimingSet.blocks.forEach((block, index) => {
      const blockShape = {
        dayOfWeek: dayCode,
        period: block.period,
        start: block.start,
        end: block.end,
      };
      const existingBlock = existingBlockMap.get(buildBlockMatchKey(blockShape));

      mappedBlocks.push(createAssignmentState({
        ...blockShape,
        assignmentType: existingBlock?.assignmentType || 'TEXT',
        selectedUserOption: existingBlock?.selectedUserOption || null,
        textValue: existingBlock?.textValue || '',
        notes: existingBlock?.notes || '',
      }, index, `${prefix}-${timingSetId}-${dayCode}`));
    });
  });

  return mappedBlocks;
}

function mapWeekVariants(weekRules, blocksFactory) {
  const weekLabels = Array.isArray(weekRules?.weekLabels) ? weekRules.weekLabels : [];
  return weekLabels.map((weekItem) => createWeekVariant(
    weekItem.weekNumber,
    weekItem.label,
    blocksFactory(weekItem.weekNumber),
    'variant',
  ));
}

function validateDetailsStage(formState) {
  const cleanRotaName = String(formState.rotaName || '').trim();

  if (!cleanRotaName) {
    return '❌ Rota name is required.';
  }

  return '';
}

function validateCycleStage(formState, timetableWeekRules) {
  if (formState.rotaType === 'LESSON_BASED' && !String(formState.timingSetId || '').trim()) {
    return '❌ Select a timetable timing set for a lesson based rota.';
  }

  if (!Array.isArray(formState.appliesTo.daysOfWeek) || formState.appliesTo.daysOfWeek.length === 0) {
    return '❌ Select at least one day of the week.';
  }

  if (formState.appliesTo.weekMode === 'SPECIFIC_WEEKS' && !String(formState.appliesTo.weekNumbersText || '').trim()) {
    return '❌ Enter one or more timetable week numbers.';
  }

  if (formState.creationMode === 'TIMETABLE_ALIGNED' && !timetableWeekRules?.usesMultiWeekCycle) {
    return '❌ Timetable aligned rotas need timetable week labels to be available.';
  }

  return '';
}

export default function SysManagerRotas() {
  const navigate = useNavigate();
  const { masterRotaId } = useParams();
  const sysManagerPerms = userAppData?.sysManager || {};
  const isEditMode = Boolean(masterRotaId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timingSets, setTimingSets] = useState([]);
  const [timetableWeekRules, setTimetableWeekRules] = useState({});
  const [formState, setFormState] = useState(createInitialFormState);
  const [staffDefaultOptions, setStaffDefaultOptions] = useState([]);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [assignmentModalState, setAssignmentModalState] = useState({
    isOpen: false,
    weekNumber: null,
    clientBlockId: '',
    blockIds: [],
    rowLabel: '',
  });
  const [assignmentModalMode, setAssignmentModalMode] = useState('TEXT');
  const [assignmentModalUserOption, setAssignmentModalUserOption] = useState(null);
  const [assignmentModalTextValue, setAssignmentModalTextValue] = useState('');
  const [assignmentModalNotes, setAssignmentModalNotes] = useState('');

  const selectedTimingSet = useMemo(
    () => timingSets.find((timingSet) => timingSet.timingSetId === formState.timingSetId) || null,
    [timingSets, formState.timingSetId],
  );
  const usesMultiWeekCycle = Boolean(timetableWeekRules?.usesMultiWeekCycle);
  const activeStage = CREATE_STAGE_OPTIONS[currentStageIndex] || CREATE_STAGE_OPTIONS[0];
  const stageSummary = useMemo(() => {
    const rotaTypeLabel = formState.rotaType === 'LESSON_BASED' ? 'Timetable based' : 'Custom times';
    const cycleLabel = formState.creationMode === 'TIMETABLE_ALIGNED'
      ? 'Different rota for each timetable week'
      : 'One rota using the Applies To rules';
    const blockCount = formState.creationMode === 'TIMETABLE_ALIGNED'
      ? (Array.isArray(formState.weekVariants)
        ? formState.weekVariants.reduce((total, variant) => total + (Array.isArray(variant.blocks) ? variant.blocks.length : 0), 0)
        : 0)
      : (Array.isArray(formState.blocks) ? formState.blocks.length : 0);

    return {
      rotaName: String(formState.rotaName || '').trim() || 'Untitled rota',
      status: String(formState.status || 'ACTIVE').trim().toUpperCase(),
      rotaTypeLabel,
      cycleLabel,
      appliesTo: formatAppliesToSummary({
        ...formState.appliesTo,
        weekNumbers: String(formState.appliesTo.weekNumbersText || '').trim()
          .split(',')
          .map((value) => Number(String(value).trim()))
          .filter((value) => Number.isInteger(value) && value > 0),
      }),
      blockCount,
    };
  }, [formState]);
  const selectedAssignmentBlock = useMemo(() => {
    if (!assignmentModalState.clientBlockId) {
      return null;
    }

    if (assignmentModalState.weekNumber !== null) {
      const selectedVariant = (Array.isArray(formState.weekVariants) ? formState.weekVariants : [])
        .find((variant) => variant.weekNumber === assignmentModalState.weekNumber);
      return selectedVariant?.blocks?.find((block) => block.clientBlockId === assignmentModalState.clientBlockId) || null;
    }

    return (Array.isArray(formState.blocks) ? formState.blocks : [])
      .find((block) => block.clientBlockId === assignmentModalState.clientBlockId) || null;
  }, [assignmentModalState, formState.blocks, formState.weekVariants]);
  const selectedAssignmentWeekLabel = useMemo(() => {
    if (assignmentModalState.weekNumber === null) {
      return '';
    }

    return (Array.isArray(formState.weekVariants) ? formState.weekVariants : [])
      .find((variant) => variant.weekNumber === assignmentModalState.weekNumber)?.weekLabel || '';
  }, [assignmentModalState.weekNumber, formState.weekVariants]);

  useEffect(() => {
    if (!sysManagerPerms || sysManagerPerms.perms !== 1 || !sysManagerPerms.rotaAdmin) {
      navigate('/app');
      return;
    }

    async function loadBootstrap() {
      setLoading(true);
      try {
        const response = await getRotasBootstrap(masterRotaId || '');
        const loadedTimingSets = Array.isArray(response?.timetableTimings) ? response.timetableTimings : [];
        const loadedWeekRules = response?.timetableWeekRules || {};

        setTimingSets(loadedTimingSets);
        setTimetableWeekRules(loadedWeekRules);

        const loadedRotaDetail = response?.rotaDetail;
        if (isEditMode) {
          if (!loadedRotaDetail) {
            toast.error('❌ Unable to load this rota for editing.', TOAST_CONFIG);
            navigate('/app/sysmanager/rotas');
            return;
          }
          setFormState(mapRotaDetailToFormState(loadedRotaDetail));
        } else {
          const defaultTimingSetId = loadedTimingSets[0]?.timingSetId || '';
          const defaultDays = [...DEFAULT_DAYS];
          const defaultBlocks = defaultTimingSetId
            ? mapTimingSetBlocks(defaultTimingSetId, loadedTimingSets, defaultDays)
            : [];
          setFormState((previousState) => ({
            ...previousState,
            timingSetId: defaultTimingSetId,
            appliesTo: {
              ...previousState.appliesTo,
              daysOfWeek: defaultDays,
            },
            blocks: defaultBlocks,
            weekVariants: loadedWeekRules?.usesMultiWeekCycle
              ? mapWeekVariants(loadedWeekRules, () => defaultBlocks)
              : [],
          }));
        }

        try {
          const staffResponse = await searchRotaStaff('');
          const staffOptions = Array.isArray(staffResponse?.items)
            ? staffResponse.items.map((item) => ({
              value: item.cogId,
              label: item.displayName,
            }))
            : [];
          setStaffDefaultOptions(staffOptions);
        } catch (staffError) {
          console.error('Failed to preload rota staff options', staffError);
        }
      } catch (error) {
        console.error('Failed to load rota bootstrap', error);
        toast.error('❌ Failed to load rota data. Please refresh and try again.', TOAST_CONFIG);
      } finally {
        setLoading(false);
      }
    }

    loadBootstrap();
  }, [isEditMode, masterRotaId, navigate, sysManagerPerms]);

  async function loadStaffOptions(inputValue = '') {
    try {
      const response = await searchRotaStaff(inputValue);
      return Array.isArray(response?.items)
        ? response.items.map((item) => ({
          value: item.cogId,
          label: item.displayName,
        }))
        : [];
    } catch (error) {
      console.error('Failed to load rota staff options', error);
      return [];
    }
  }

  function updateFormField(fieldName, value) {
    setFormState((previousState) => ({
      ...previousState,
      [fieldName]: value,
    }));
  }

  function updateAppliesToField(fieldName, value) {
    setFormState((previousState) => ({
      ...previousState,
      appliesTo: {
        ...previousState.appliesTo,
        [fieldName]: value,
      },
    }));
  }

  function handleCreationModeChange(nextMode) {
    setFormState((previousState) => ({
      ...previousState,
      creationMode: nextMode,
      weekVariants: (
        nextMode === 'TIMETABLE_ALIGNED' && timetableWeekRules?.usesMultiWeekCycle
      )
        ? mapWeekVariants(
          timetableWeekRules,
          () => (
            previousState.rotaType === 'CUSTOM'
              ? [createAssignmentState({ dayOfWeek: previousState.appliesTo.daysOfWeek[0] || '' }, 0, 'custom')]
              : mapTimingSetBlocks(
                previousState.timingSetId,
                timingSets,
                previousState.appliesTo.daysOfWeek,
                previousState.blocks,
              )
          ),
        )
        : previousState.weekVariants,
    }));
  }

  function handleRotaTypeChange(nextType) {
    setFormState((previousState) => {
      if (nextType === 'CUSTOM') {
        const defaultCustomBlocks = previousState.blocks.length > 0 && previousState.rotaType === 'CUSTOM'
          ? previousState.blocks
          : [createAssignmentState({ dayOfWeek: previousState.appliesTo.daysOfWeek[0] || '' }, 0, 'custom')];
        return {
          ...previousState,
          rotaType: 'CUSTOM',
          blocks: defaultCustomBlocks,
          weekVariants: timetableWeekRules?.usesMultiWeekCycle
            ? mapWeekVariants(timetableWeekRules, () => defaultCustomBlocks)
            : previousState.weekVariants,
        };
      }

      const nextTimingSetId = previousState.timingSetId || timingSets[0]?.timingSetId || '';
      const nextBlocks = nextTimingSetId
        ? mapTimingSetBlocks(
          nextTimingSetId,
          timingSets,
          previousState.appliesTo.daysOfWeek,
          previousState.blocks,
        )
        : [];
      return {
        ...previousState,
        rotaType: 'LESSON_BASED',
        timingSetId: nextTimingSetId,
        blocks: nextBlocks,
        weekVariants: timetableWeekRules?.usesMultiWeekCycle
          ? mapWeekVariants(timetableWeekRules, () => nextBlocks)
          : previousState.weekVariants,
      };
    });
  }

  function handleTimingSetChange(nextTimingSetId) {
    setFormState((previousState) => ({
      ...previousState,
      timingSetId: nextTimingSetId,
      blocks: mapTimingSetBlocks(
        nextTimingSetId,
        timingSets,
        previousState.appliesTo.daysOfWeek,
        previousState.blocks,
      ),
      weekVariants: timetableWeekRules?.usesMultiWeekCycle
        ? mapWeekVariants(
          timetableWeekRules,
          (weekNumber) => {
            const currentVariant = previousState.weekVariants.find((variant) => variant.weekNumber === weekNumber);
            return mapTimingSetBlocks(
              nextTimingSetId,
              timingSets,
              previousState.appliesTo.daysOfWeek,
              currentVariant?.blocks || previousState.blocks,
              `variant-${weekNumber}`,
            );
          },
        )
        : previousState.weekVariants,
    }));
  }

  function applyLessonBasedDayChange(nextDays) {
    setFormState((previousState) => ({
      ...previousState,
      appliesTo: {
        ...previousState.appliesTo,
        daysOfWeek: nextDays,
      },
      blocks: previousState.rotaType === 'LESSON_BASED'
        ? mapTimingSetBlocks(
          previousState.timingSetId,
          timingSets,
          nextDays,
          previousState.blocks,
        )
        : previousState.blocks,
      weekVariants: timetableWeekRules?.usesMultiWeekCycle
        ? mapWeekVariants(
          timetableWeekRules,
          (weekNumber) => {
            const currentVariant = previousState.weekVariants.find((variant) => variant.weekNumber === weekNumber);

            if (previousState.rotaType === 'CUSTOM') {
              return currentVariant?.blocks || previousState.blocks;
            }

            return mapTimingSetBlocks(
              previousState.timingSetId,
              timingSets,
              nextDays,
              currentVariant?.blocks || previousState.blocks,
              `variant-${weekNumber}`,
            );
          },
        )
        : previousState.weekVariants,
    }));
  }

  function updateBlock(clientBlockId, fieldName, value) {
    setFormState((previousState) => ({
      ...previousState,
      blocks: previousState.blocks.map((block) => {
        if (block.clientBlockId !== clientBlockId) {
          return block;
        }

        const updatedBlock = {
          ...block,
          [fieldName]: value,
        };

        if (fieldName === 'assignmentType' && value === 'USER') {
          updatedBlock.textValue = '';
        }

        if (fieldName === 'assignmentType' && value === 'TEXT') {
          updatedBlock.selectedUserOption = null;
        }

        return updatedBlock;
      }),
    }));
  }

  function updateVariantBlock(weekNumber, clientBlockId, fieldName, value) {
    setFormState((previousState) => ({
      ...previousState,
      weekVariants: previousState.weekVariants.map((variant) => {
        if (variant.weekNumber !== weekNumber) {
          return variant;
        }

        return {
          ...variant,
          blocks: variant.blocks.map((block) => {
            if (block.clientBlockId !== clientBlockId) {
              return block;
            }

            const updatedBlock = {
              ...block,
              [fieldName]: value,
            };

            if (fieldName === 'assignmentType' && value === 'USER') {
              updatedBlock.textValue = '';
            }

            if (fieldName === 'assignmentType' && value === 'TEXT') {
              updatedBlock.selectedUserOption = null;
            }

            return updatedBlock;
          }),
        };
      }),
    }));
  }

  function addCustomBlock() {
    setFormState((previousState) => ({
      ...previousState,
      blocks: [
        ...previousState.blocks,
        createAssignmentState({ dayOfWeek: previousState.appliesTo.daysOfWeek[0] || '' }, previousState.blocks.length, 'custom'),
      ],
    }));
  }

  function removeCustomBlock(clientBlockId) {
    setFormState((previousState) => ({
      ...previousState,
      blocks: previousState.blocks.filter((block) => block.clientBlockId !== clientBlockId),
    }));
  }

  function addVariantCustomBlock(weekNumber) {
    setFormState((previousState) => ({
      ...previousState,
      weekVariants: previousState.weekVariants.map((variant) => (
        variant.weekNumber === weekNumber
          ? {
            ...variant,
            blocks: [
              ...variant.blocks,
              createAssignmentState({ dayOfWeek: previousState.appliesTo.daysOfWeek[0] || '' }, variant.blocks.length, `custom-${weekNumber}`),
            ],
          }
          : variant
      )),
    }));
  }

  function removeVariantCustomBlock(weekNumber, clientBlockId) {
    setFormState((previousState) => ({
      ...previousState,
      weekVariants: previousState.weekVariants.map((variant) => {
        if (variant.weekNumber !== weekNumber) {
          return variant;
        }
        return {
          ...variant,
          blocks: variant.blocks.filter((block) => block.clientBlockId !== clientBlockId),
        };
      }),
    }));
  }

  function toggleDay(dayValue) {
    const currentDays = Array.isArray(formState.appliesTo.daysOfWeek)
      ? formState.appliesTo.daysOfWeek
      : [];

    const nextDays = currentDays.includes(dayValue)
      ? currentDays.filter((value) => value !== dayValue)
      : [...currentDays, dayValue];

    if (formState.rotaType === 'LESSON_BASED') {
      applyLessonBasedDayChange(nextDays);
      return;
    }

    setFormState((previousState) => {
      const currentDays = Array.isArray(previousState.appliesTo.daysOfWeek)
        ? previousState.appliesTo.daysOfWeek
        : [];

      const nextDays = currentDays.includes(dayValue)
        ? currentDays.filter((value) => value !== dayValue)
        : [...currentDays, dayValue];

      return {
        ...previousState,
        appliesTo: {
          ...previousState.appliesTo,
          daysOfWeek: nextDays,
        },
      };
    });
  }

  function moveToStage(stageIndex) {
    if (stageIndex < 0 || stageIndex >= CREATE_STAGE_OPTIONS.length) {
      return;
    }

    setCurrentStageIndex(stageIndex);
  }

  function handleNextStage() {
    if (currentStageIndex === 0) {
      const detailsError = validateDetailsStage(formState);
      if (detailsError) {
        toast.error(detailsError, TOAST_CONFIG);
        return;
      }
    }

    if (currentStageIndex === 1) {
      const cycleError = validateCycleStage(formState, timetableWeekRules);
      if (cycleError) {
        toast.error(cycleError, TOAST_CONFIG);
        return;
      }
    }

    moveToStage(currentStageIndex + 1);
  }

  function openAssignmentModal(block, weekNumber = null) {
    setAssignmentModalState({
      isOpen: true,
      weekNumber,
      clientBlockId: block.clientBlockId,
      blockIds: [block.clientBlockId],
      rowLabel: '',
    });
    setAssignmentModalMode('USER');
    setAssignmentModalUserOption(block.selectedUserOption || null);
    setAssignmentModalTextValue(String(block.textValue || '').trim());
    setAssignmentModalNotes(String(block.notes || ''));
  }

  function closeAssignmentModal() {
    setAssignmentModalState({
      isOpen: false,
      weekNumber: null,
      clientBlockId: '',
      blockIds: [],
      rowLabel: '',
    });
    setAssignmentModalMode('TEXT');
    setAssignmentModalUserOption(null);
    setAssignmentModalTextValue('');
    setAssignmentModalNotes('');
  }

  function handleAssignmentModalSave() {
    if (!selectedAssignmentBlock) {
      closeAssignmentModal();
      return;
    }

    if (assignmentModalMode === 'USER' && !assignmentModalUserOption?.value) {
      toast.error('❌ Select a staff member before saving this slot.', TOAST_CONFIG);
      return;
    }

    if (assignmentModalMode === 'TEXT' && !String(assignmentModalTextValue || '').trim()) {
      toast.error('❌ Enter a text value before saving this slot.', TOAST_CONFIG);
      return;
    }

    const assignmentType = assignmentModalMode === 'USER' ? 'USER' : 'TEXT';
    const selectedUserOption = assignmentModalMode === 'USER' ? assignmentModalUserOption : null;
    const textValue = assignmentModalMode === 'TEXT'
      ? String(assignmentModalTextValue || '').trim()
      : assignmentModalMode === 'NONE'
      ? 'None'
      : '';

    const targetBlockIds = assignmentModalState.blockIds.length > 0
      ? assignmentModalState.blockIds
      : [selectedAssignmentBlock.clientBlockId];

    if (assignmentModalState.weekNumber !== null) {
      targetBlockIds.forEach((blockId) => {
        updateVariantBlock(assignmentModalState.weekNumber, blockId, 'assignmentType', assignmentType);
        updateVariantBlock(assignmentModalState.weekNumber, blockId, 'selectedUserOption', selectedUserOption);
        updateVariantBlock(assignmentModalState.weekNumber, blockId, 'textValue', textValue);
        updateVariantBlock(assignmentModalState.weekNumber, blockId, 'notes', assignmentModalNotes);
      });
    } else {
      targetBlockIds.forEach((blockId) => {
        updateBlock(blockId, 'assignmentType', assignmentType);
        updateBlock(blockId, 'selectedUserOption', selectedUserOption);
        updateBlock(blockId, 'textValue', textValue);
        updateBlock(blockId, 'notes', assignmentModalNotes);
      });
    }

    closeAssignmentModal();
  }

  function openSetAllAssignmentModal(row, weekNumber = null) {
    const rowBlocks = Object.values(row?.cells || {}).filter(Boolean);
    const firstRowBlock = rowBlocks[0];

    if (!firstRowBlock) {
      return;
    }

    setAssignmentModalState({
      isOpen: true,
      weekNumber,
      clientBlockId: firstRowBlock.clientBlockId,
      blockIds: rowBlocks.map((block) => block.clientBlockId),
      rowLabel: `${row.period} | ${row.start} - ${row.end}`,
    });
    setAssignmentModalMode('USER');
    setAssignmentModalUserOption(firstRowBlock.selectedUserOption || null);
    setAssignmentModalTextValue(String(firstRowBlock.textValue || '').trim());
    setAssignmentModalNotes(String(firstRowBlock.notes || ''));
  }

  async function handleSaveRota() {
    const detailsError = validateDetailsStage(formState);
    if (detailsError) {
      setCurrentStageIndex(0);
      toast.error(detailsError, TOAST_CONFIG);
      return;
    }

    const cycleError = validateCycleStage(formState, timetableWeekRules);
    if (cycleError) {
      setCurrentStageIndex(1);
      toast.error(cycleError, TOAST_CONFIG);
      return;
    }

    if (formState.creationMode === 'TIMETABLE_ALIGNED') {
      const variants = Array.isArray(formState.weekVariants) ? formState.weekVariants : [];
      if (variants.length === 0) {
        setCurrentStageIndex(1);
        toast.error('❌ This school uses a multi-week timetable, so aligned rotas need one rota per timetable week.', TOAST_CONFIG);
        return;
      }
      for (const variant of variants) {
        const blockError = validateBlocks(variant.blocks, formState.rotaType, formState.appliesTo.daysOfWeek);
        if (blockError) {
          setCurrentStageIndex(2);
          toast.error(`❌ ${variant.weekLabel}: ${blockError}`, TOAST_CONFIG);
          return;
        }
      }
    } else {
      const blockError = validateBlocks(formState.blocks, formState.rotaType, formState.appliesTo.daysOfWeek);
      if (blockError) {
        setCurrentStageIndex(2);
        toast.error(`❌ ${blockError}`, TOAST_CONFIG);
        return;
      }
    }

    const payload = buildPayload(formState);

    setSaving(true);
    try {
      const response = await saveRota(payload);
      const createdRotas = Array.isArray(response?.rotas) ? response.rotas : [];
      const membershipSyncWarnings = Array.isArray(response?.membershipSyncWarnings)
        ? response.membershipSyncWarnings
        : [];

      if (!isEditMode) {
        const resetBlocks = timingSets[0]?.timingSetId
          ? mapTimingSetBlocks(timingSets[0].timingSetId, timingSets, DEFAULT_DAYS)
          : [];
        setFormState(() => ({
          ...createInitialFormState(),
          timingSetId: timingSets[0]?.timingSetId || '',
          blocks: resetBlocks,
          weekVariants: timetableWeekRules?.usesMultiWeekCycle
            ? mapWeekVariants(timetableWeekRules, () => resetBlocks)
            : [],
        }));
        setCurrentStageIndex(0);
      }

      if (membershipSyncWarnings.length > 0) {
        toast.success(`✅ Rota ${isEditMode ? 'updated' : 'created'}, but some membership cache updates need checking.`, TOAST_CONFIG);
      } else if (createdRotas.length > 1) {
        toast.success(`✅ Timetable-cycle rotas ${isEditMode ? 'updated' : 'created'} successfully.`, TOAST_CONFIG);
      } else {
        toast.success(`✅ Rota ${isEditMode ? 'updated' : 'created'} successfully.`, TOAST_CONFIG);
      }
      navigate('/app/sysmanager/rotas');
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} rota`, error);
      toast.error(`❌ Failed to ${isEditMode ? 'update' : 'create'} rota. Please check the form and try again.`, TOAST_CONFIG);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_rotas_container'>
        <Header
          title='Rotas'
          subtitle={isEditMode
            ? 'Update the saved rota details, dates, timetable setup, and assignments using the same guided flow as creation.'
            : 'Create timetable-based or custom rotas, assign staff or text blocks, and store them in a consistent format for use across the app.'}
          rightContent={(
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager/rotas')}>
              <FaArrowLeft aria-hidden='true' />
              Back to Rotas
            </button>
          )}
        />

        {loading ? (
          <section className='sysManager_rotas_loadingCard'>
            <CgSpinner className='sysManager_rotas_spinner' />
            <p>Loading rota setup data...</p>
          </section>
        ) : (
          <section className='sysManager_rotas_builderCard sysManager_rotas_builderCard--main'>
              <div className='sysManager_rotas_cardHeader'>
                <div className='sysManager_rotas_iconWrap'>
                  <BsCalendar2WeekFill color='#c48e38' size={42} />
                </div>
                <div>
                  <h2 className='sysManager_rotas_cardTitle'>{isEditMode ? 'Edit Rota' : 'Create New Rota'}</h2>
                  <p className='sysManager_rotas_cardText'>
                    {isEditMode
                      ? 'Review the existing rota setup, adjust the dates or naming, then update the staff or text assigned to each block. Multi-week rota families are edited together in one save.'
                      : 'Start with a timetable timing set or define a custom rota, then assign either a named staff member or plain text to each block. Multi-week schools can create one rota per timetable week in a single save.'}
                  </p>
                </div>
              </div>

              <div className='sysManager_rotas_stageBar'>
                {CREATE_STAGE_OPTIONS.map((stageOption, index) => {
                  const isActive = index === currentStageIndex;
                  const isComplete = index < currentStageIndex;
                  const stageClassName = [
                    'sysManager_rotas_stageButton',
                    isActive ? 'sysManager_rotas_stageButton--active' : '',
                    isComplete ? 'sysManager_rotas_stageButton--complete' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <button
                      key={stageOption.id}
                      className={stageClassName}
                      type='button'
                      onClick={() => moveToStage(index)}
                    >
                      <span className='sysManager_rotas_stageDot'>{index + 1}</span>
                      <span className='sysManager_rotas_stageLabel'>{stageOption.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className='sysManager_rotas_stagePanel'>
                <div className={activeStage.id === 'BLOCKS' ? 'sysManager_rotas_stageIntro sysManager_rotas_stageIntro--centered' : 'sysManager_rotas_stageIntro'}>
                  <div>
                    <p className='sysManager_rotas_stageEyebrow'>
                      Step {currentStageIndex + 1} of {CREATE_STAGE_OPTIONS.length}
                    </p>
                    <h3>{activeStage.label}</h3>
                    <p>
                      {activeStage.id === 'DETAILS' && 'Start with the rota name, status, and whether the rota follows timetable periods or custom times.'}
                      {activeStage.id === 'CYCLE' && 'Choose how often the rota should apply. If this is a timetable based rota, select the timing set here before setting the cycle rules.'}
                      {activeStage.id === 'BLOCKS' && 'Assign the actual people or text values for each block. You can still jump back to earlier steps at any time and this stage will refresh around those choices.'}
                    </p>
                  </div>

                  {activeStage.id !== 'DETAILS' && (
                    <div className='sysManager_rotas_summaryGrid'>
                      <div className='sysManager_rotas_summaryItem'>
                        <span>Name</span>
                        <strong>{stageSummary.rotaName}</strong>
                      </div>
                      <div className='sysManager_rotas_summaryItem'>
                        <span>Status</span>
                        <strong>{stageSummary.status}</strong>
                      </div>
                      <div className='sysManager_rotas_summaryItem'>
                        <span>Type</span>
                        <strong>{stageSummary.rotaTypeLabel}</strong>
                      </div>
                      <div className='sysManager_rotas_summaryItem'>
                        <span>Cycle</span>
                        <strong>{stageSummary.cycleLabel}</strong>
                      </div>
                    </div>
                  )}
                </div>

                {activeStage.id === 'DETAILS' && (
                  <div className='sysManager_rotas_formGrid'>
                    <div className='sysManager_rotas_field'>
                      <label htmlFor='rotas-rotaName'>Rota name</label>
                      <input
                        id='rotas-rotaName'
                        type='text'
                        value={formState.rotaName}
                        onChange={(event) => updateFormField('rotaName', event.target.value)}
                        placeholder='Lunch duty rota'
                      />
                    </div>

                    <div className='sysManager_rotas_field'>
                      <label htmlFor='rotas-description'>Description</label>
                      <input
                        id='rotas-description'
                        type='text'
                        value={formState.description}
                        onChange={(event) => updateFormField('description', event.target.value)}
                        placeholder='Optional notes about how this rota is used.'
                      />
                    </div>

                    <div className='sysManager_rotas_field'>
                      <label htmlFor='rotas-status'>Status</label>
                      <select
                        id='rotas-status'
                        value={formState.status}
                        onChange={(event) => updateFormField('status', event.target.value)}
                      >
                        {STATUS_OPTIONS.map((statusOption) => (
                          <option key={statusOption} value={statusOption}>{statusOption}</option>
                        ))}
                      </select>
                    </div>

                    <div className='sysManager_rotas_field'>
                      <label htmlFor='rotas-type'>Rota type</label>
                      <select
                        id='rotas-type'
                        value={formState.rotaType}
                        onChange={(event) => handleRotaTypeChange(event.target.value)}
                      >
                        <option value='LESSON_BASED'>Timetable based</option>
                        <option value='CUSTOM'>Custom times</option>
                      </select>
                    </div>

                    <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                      <span className='sysManager_rotas_toggleLabel'>Available on users dashboard</span>
                      <label
                        className={`sysManager_rotas_toggleCard ${formState.availableOnDashboard ? 'sysManager_rotas_toggleCard--active' : ''}`}
                        htmlFor='rotas-availableOnDashboard'
                      >
                        <input
                          id='rotas-availableOnDashboard'
                          type='checkbox'
                          checked={Boolean(formState.availableOnDashboard)}
                          onChange={(event) => updateFormField('availableOnDashboard', event.target.checked)}
                        />
                        <span className={`sysManager_rotas_toggleSwitch ${formState.availableOnDashboard ? 'sysManager_rotas_toggleSwitch--active' : ''}`}>
                          <span className='sysManager_rotas_toggleKnob' />
                        </span>
                        <span className='sysManager_rotas_toggleContent'>
                          <span className='sysManager_rotas_toggleTitle'>
                            {formState.availableOnDashboard ? 'Shown on user dashboards' : 'Hidden from user dashboards'}
                          </span>
                          <span className='sysManager_rotas_helperText'>
                            This is the master rota-family switch. Users only see a dashboard button when this is on and their own access entry also allows it.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {activeStage.id === 'CYCLE' && (
                  <div className='sysManager_rotas_subCard sysManager_rotas_subCard--stage'>
                    <h3>Applies To</h3>
                    <div className='sysManager_rotas_formGrid'>
                      {formState.rotaType === 'LESSON_BASED' && (
                        <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                          <label htmlFor='rotas-timingSet'>Timetable timing set</label>
                          <select
                            id='rotas-timingSet'
                            value={formState.timingSetId}
                            onChange={(event) => handleTimingSetChange(event.target.value)}
                          >
                            {timingSets.map((timingSet) => (
                              <option key={timingSet.timingSetId} value={timingSet.timingSetId}>
                                {timingSet.label}
                              </option>
                            ))}
                          </select>
                          {selectedTimingSet && (
                            <p className='sysManager_rotas_helperText'>
                              {selectedTimingSet.blocks.length} timetable periods will be copied into the rota.
                            </p>
                          )}
                        </div>
                      )}

                      {usesMultiWeekCycle && (
                        <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                          <label htmlFor='rotas-creationMode'>Timetable cycle mode</label>
                          <select
                            id='rotas-creationMode'
                            value={formState.creationMode}
                            onChange={(event) => handleCreationModeChange(event.target.value)}
                          >
                            <option value='EVERY_WEEK'>Every week rota</option>
                            <option value='TIMETABLE_ALIGNED'>Different rota for each timetable week</option>
                          </select>
                          <p className='sysManager_rotas_helperText'>
                            {formState.creationMode === 'TIMETABLE_ALIGNED'
                              ? 'A separate master rota will be created for each timetable week label below.'
                              : 'This rota will use the Applies To values exactly as entered below.'}
                          </p>
                        </div>
                      )}

                      <div className='sysManager_rotas_field'>
                        <label htmlFor='rotas-weekMode'>Week mode</label>
                        <select
                          id='rotas-weekMode'
                          value={formState.appliesTo.weekMode}
                          onChange={(event) => updateAppliesToField('weekMode', event.target.value)}
                          disabled={formState.creationMode === 'TIMETABLE_ALIGNED'}
                        >
                          <option value='ALL_WEEKS'>All weeks</option>
                          <option value='SPECIFIC_WEEKS'>Specific weeks</option>
                        </select>
                      </div>

                      <div className='sysManager_rotas_field'>
                        <label htmlFor='rotas-weekNumbers'>Week numbers</label>
                        <input
                          id='rotas-weekNumbers'
                          type='text'
                          value={formState.appliesTo.weekNumbersText}
                          onChange={(event) => updateAppliesToField('weekNumbersText', event.target.value)}
                          placeholder='1, 2'
                          disabled={formState.creationMode === 'TIMETABLE_ALIGNED' || formState.appliesTo.weekMode !== 'SPECIFIC_WEEKS'}
                        />
                      </div>

                      <div className='sysManager_rotas_field'>
                        <label htmlFor='rotas-dateFrom'>Date from</label>
                        <input
                          id='rotas-dateFrom'
                          type='date'
                          value={formState.appliesTo.dateFrom}
                          onChange={(event) => updateAppliesToField('dateFrom', event.target.value)}
                        />
                      </div>

                      <div className='sysManager_rotas_field'>
                        <label htmlFor='rotas-dateTo'>Date to</label>
                        <input
                          id='rotas-dateTo'
                          type='date'
                          value={formState.appliesTo.dateTo}
                          onChange={(event) => updateAppliesToField('dateTo', event.target.value)}
                        />
                      </div>

                      <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                        <label>Days of week</label>
                        <div className='sysManager_rotas_dayPills'>
                          {DAY_OPTIONS.map((dayOption) => {
                            const isActive = formState.appliesTo.daysOfWeek.includes(dayOption.value);
                            return (
                              <button
                                key={dayOption.value}
                                className={isActive ? 'sysManager_rotas_dayPill sysManager_rotas_dayPill--active' : 'sysManager_rotas_dayPill'}
                                type='button'
                                onClick={() => toggleDay(dayOption.value)}
                              >
                                {dayOption.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className='sysManager_rotas_cyclePreview'>
                      <span>Current applies-to summary</span>
                      <strong>{stageSummary.appliesTo}</strong>
                    </div>
                  </div>
                )}

                {activeStage.id === 'BLOCKS' && (
                  <div className='sysManager_rotas_subCard sysManager_rotas_subCard--stage'>
                    <div className='sysManager_rotas_subCardHeader'>
                      <div>
                        <h3>Rota blocks</h3>
                        <p>
                          {formState.creationMode === 'TIMETABLE_ALIGNED'
                            ? 'Each timetable week below stores its own rota so different staff or text can be set for each week in the cycle.'
                            : formState.rotaType === 'LESSON_BASED'
                            ? 'Each timetable period needs either a named person or a text value.'
                            : 'Custom duty zones are day-specific. Different named zones may run at the same time, while the same zone cannot overlap itself on one day.'}
                        </p>
                      </div>
                      <div className='sysManager_rotas_stageMeta'>
                        <span>{stageSummary.blockCount} blocks ready</span>
                        {formState.rotaType === 'CUSTOM' && formState.creationMode !== 'TIMETABLE_ALIGNED' && (
                          <button
                            className='sysManager_rotas_secondaryButton'
                            type='button'
                            onClick={addCustomBlock}
                          >
                            Add custom row
                          </button>
                        )}
                      </div>
                    </div>

                    <div className='sysManager_rotas_blocksTable'>
                      {formState.creationMode === 'TIMETABLE_ALIGNED' ? formState.weekVariants.map((variant) => (
                        <div className='sysManager_rotas_variantSection' key={variant.weekNumber}>
                          <div className={formState.rotaType === 'LESSON_BASED' ? 'sysManager_rotas_subCardHeader sysManager_rotas_subCardHeader--centered' : 'sysManager_rotas_subCardHeader'}>
                            <div className={formState.rotaType === 'LESSON_BASED' ? 'sysManager_rotas_subCardHeaderContent--centered' : ''}>
                              <h3>{variant.weekLabel}</h3>
                              <p>{isEditMode ? 'Updates:' : 'Creates:'} {formState.rotaName || 'New rota'} - {variant.weekLabel}</p>
                            </div>
                            {formState.rotaType === 'CUSTOM' && (
                              <button
                                className='sysManager_rotas_secondaryButton'
                                type='button'
                                onClick={() => addVariantCustomBlock(variant.weekNumber)}
                              >
                                Add row
                              </button>
                            )}
                          </div>

                          {formState.rotaType === 'LESSON_BASED' ? (
                            <SysManagerRotasTimetableGrid
                              blocks={variant.blocks}
                              dayCodes={formState.appliesTo.daysOfWeek}
                              onCellClick={(block) => openAssignmentModal(block, variant.weekNumber)}
                              onSetAllClick={(row) => openSetAllAssignmentModal(row, variant.weekNumber)}
                            />
                          ) : variant.blocks.map((block, index) => (
                            <div className='sysManager_rotas_blockRow' key={block.clientBlockId}>
                              <div className='sysManager_rotas_blockMeta'>
                                <div className='sysManager_rotas_field'>
                                  <label>Day</label>
                                  <select
                                    value={block.dayOfWeek}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'dayOfWeek', event.target.value)}
                                  >
                                    <option value=''>Select day</option>
                                    {DAY_OPTIONS.filter((dayOption) => formState.appliesTo.daysOfWeek.includes(dayOption.value)).map((dayOption) => (
                                      <option key={dayOption.value} value={dayOption.value}>{dayOption.label}</option>
                                    ))}
                                  </select>
                                </div>

                                <div className='sysManager_rotas_field'>
                                  <label>Duty zone / label</label>
                                  <input
                                    type='text'
                                    value={block.period}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'period', event.target.value)}
                                    disabled={formState.rotaType === 'LESSON_BASED'}
                                  />
                                </div>

                                <div className='sysManager_rotas_field'>
                                  <label>Start</label>
                                  <input
                                    type='time'
                                    value={block.start}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'start', event.target.value)}
                                    disabled={formState.rotaType === 'LESSON_BASED'}
                                  />
                                </div>

                                <div className='sysManager_rotas_field'>
                                  <label>End</label>
                                  <input
                                    type='time'
                                    value={block.end}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'end', event.target.value)}
                                    disabled={formState.rotaType === 'LESSON_BASED'}
                                  />
                                </div>

                                <div className='sysManager_rotas_field'>
                                  <label>Assignment type</label>
                                  <select
                                    value={block.assignmentType}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'assignmentType', event.target.value)}
                                  >
                                    <option value='USER'>Named person</option>
                                    <option value='TEXT'>Text</option>
                                  </select>
                                </div>
                              </div>

                              <div className='sysManager_rotas_assignmentPanel'>
                                {block.assignmentType === 'USER' ? (
                                  <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                                    <label>Assigned staff member</label>
                                    <AsyncSelect
                                      cacheOptions
                                      defaultOptions={staffDefaultOptions}
                                      loadOptions={loadStaffOptions}
                                      value={block.selectedUserOption}
                                      onChange={(selectedOption) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'selectedUserOption', selectedOption)}
                                      className='sysManager_rotas_asyncSelect'
                                      placeholder='Start typing a staff name'
                                    />
                                  </div>
                                ) : (
                                  <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                                    <label>Text value</label>
                                    <input
                                      type='text'
                                      value={block.textValue}
                                      onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'textValue', event.target.value)}
                                      placeholder='SLT'
                                    />
                                  </div>
                                )}

                                <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                                  <label>Notes</label>
                                  <input
                                    type='text'
                                    value={block.notes}
                                    onChange={(event) => updateVariantBlock(variant.weekNumber, block.clientBlockId, 'notes', event.target.value)}
                                    placeholder='Optional notes for this rota block'
                                  />
                                </div>

                                <button
                                  className='sysManager_rotas_removeButton'
                                  type='button'
                                  onClick={() => removeVariantCustomBlock(variant.weekNumber, block.clientBlockId)}
                                  disabled={variant.blocks.length <= 1}
                                >
                                  Remove row {index + 1}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )) : formState.rotaType === 'LESSON_BASED' ? (
                        <SysManagerRotasTimetableGrid
                          blocks={formState.blocks}
                          dayCodes={formState.appliesTo.daysOfWeek}
                          onCellClick={(block) => openAssignmentModal(block)}
                          onSetAllClick={(row) => openSetAllAssignmentModal(row)}
                        />
                      ) : formState.blocks.map((block, index) => (
                        <div className='sysManager_rotas_blockRow' key={block.clientBlockId}>
                          <div className='sysManager_rotas_blockMeta'>
                            <div className='sysManager_rotas_field'>
                              <label>Day</label>
                              <select
                                value={block.dayOfWeek}
                                onChange={(event) => updateBlock(block.clientBlockId, 'dayOfWeek', event.target.value)}
                              >
                                <option value=''>Select day</option>
                                {DAY_OPTIONS.filter((dayOption) => formState.appliesTo.daysOfWeek.includes(dayOption.value)).map((dayOption) => (
                                  <option key={dayOption.value} value={dayOption.value}>{dayOption.label}</option>
                                ))}
                              </select>
                            </div>

                            <div className='sysManager_rotas_field'>
                              <label>Duty zone / label</label>
                              <input
                                type='text'
                                value={block.period}
                                onChange={(event) => updateBlock(block.clientBlockId, 'period', event.target.value)}
                                disabled={formState.rotaType === 'LESSON_BASED'}
                              />
                            </div>

                            <div className='sysManager_rotas_field'>
                              <label>Start</label>
                              <input
                                type='time'
                                value={block.start}
                                onChange={(event) => updateBlock(block.clientBlockId, 'start', event.target.value)}
                                disabled={formState.rotaType === 'LESSON_BASED'}
                              />
                            </div>

                            <div className='sysManager_rotas_field'>
                              <label>End</label>
                              <input
                                type='time'
                                value={block.end}
                                onChange={(event) => updateBlock(block.clientBlockId, 'end', event.target.value)}
                                disabled={formState.rotaType === 'LESSON_BASED'}
                              />
                            </div>

                            <div className='sysManager_rotas_field'>
                              <label>Assignment type</label>
                              <select
                                value={block.assignmentType}
                                onChange={(event) => updateBlock(block.clientBlockId, 'assignmentType', event.target.value)}
                              >
                                <option value='USER'>Named person</option>
                                <option value='TEXT'>Text</option>
                              </select>
                            </div>
                          </div>

                          <div className='sysManager_rotas_assignmentPanel'>
                            {block.assignmentType === 'USER' ? (
                              <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                                <label>Assigned staff member</label>
                                <AsyncSelect
                                  cacheOptions
                                  defaultOptions={staffDefaultOptions}
                                  loadOptions={loadStaffOptions}
                                  value={block.selectedUserOption}
                                  onChange={(selectedOption) => updateBlock(block.clientBlockId, 'selectedUserOption', selectedOption)}
                                  className='sysManager_rotas_asyncSelect'
                                  placeholder='Start typing a staff name'
                                />
                              </div>
                            ) : (
                              <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                                <label>Text value</label>
                                <input
                                  type='text'
                                  value={block.textValue}
                                  onChange={(event) => updateBlock(block.clientBlockId, 'textValue', event.target.value)}
                                  placeholder='SLT'
                                />
                              </div>
                            )}

                            <div className='sysManager_rotas_field sysManager_rotas_field--full'>
                              <label>Notes</label>
                              <input
                                type='text'
                                value={block.notes}
                                onChange={(event) => updateBlock(block.clientBlockId, 'notes', event.target.value)}
                                placeholder='Optional notes for this rota block'
                              />
                            </div>

                            <button
                              className='sysManager_rotas_removeButton'
                              type='button'
                              onClick={() => removeCustomBlock(block.clientBlockId)}
                              disabled={formState.blocks.length <= 1}
                            >
                              Remove row {index + 1}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className='sysManager_rotas_actions'>
                <button
                  className='sysManager_rotas_secondaryButton'
                  type='button'
                  onClick={() => moveToStage(currentStageIndex - 1)}
                  disabled={currentStageIndex === 0 || saving}
                >
                  Previous
                </button>

                {currentStageIndex < CREATE_STAGE_OPTIONS.length - 1 ? (
                  <button
                    className='sysManager_rotas_primaryButton'
                    type='button'
                    onClick={handleNextStage}
                    disabled={saving}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    className='sysManager_rotas_primaryButton'
                    type='button'
                    onClick={handleSaveRota}
                    disabled={saving}
                  >
                    {saving ? <CgSpinner className='sysManager_rotas_buttonSpinner' /> : isEditMode ? 'Update rota' : 'Create rota'}
                  </button>
                )}
              </div>

              <BookingQuestionsModal
                modalActiveState={assignmentModalState.isOpen}
                setStateFunction={(isOpen) => {
                  if (!isOpen) {
                    closeAssignmentModal();
                  }
                }}
              >
                <div className='sysManager_rotas_modalContent'>
                  <h2 className='sysManager_rotas_modalTitle'>Assign timetable slot</h2>
                  {selectedAssignmentBlock && (
                    <p className='sysManager_rotas_modalSubtitle'>
                      {selectedAssignmentWeekLabel ? `${selectedAssignmentWeekLabel} | ` : ''}
                      {assignmentModalState.blockIds.length > 1
                        ? `${assignmentModalState.rowLabel} | All selected days`
                        : `${DAY_LABELS[selectedAssignmentBlock.dayOfWeek] || selectedAssignmentBlock.dayOfWeek} | ${selectedAssignmentBlock.period} | ${selectedAssignmentBlock.start} - ${selectedAssignmentBlock.end}`}
                    </p>
                  )}

                  <div className='sysManager_rotas_field'>
                    <label htmlFor='rotas-assignmentMode'>Slot value</label>
                    <select
                      id='rotas-assignmentMode'
                      value={assignmentModalMode}
                      onChange={(event) => setAssignmentModalMode(event.target.value)}
                    >
                      {ASSIGNMENT_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {assignmentModalMode === 'USER' && (
                    <div className='sysManager_rotas_field'>
                      <label>Assigned staff member</label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={staffDefaultOptions}
                        loadOptions={loadStaffOptions}
                        value={assignmentModalUserOption}
                        onChange={setAssignmentModalUserOption}
                        className='sysManager_rotas_asyncSelect'
                        placeholder='Start typing a staff name'
                      />
                    </div>
                  )}

                  {assignmentModalMode === 'TEXT' && (
                    <div className='sysManager_rotas_field'>
                      <label htmlFor='rotas-assignmentText'>Text value</label>
                      <input
                        id='rotas-assignmentText'
                        type='text'
                        value={assignmentModalTextValue}
                        onChange={(event) => setAssignmentModalTextValue(event.target.value)}
                        placeholder='SLT'
                      />
                    </div>
                  )}

                  <div className='sysManager_rotas_field'>
                    <label htmlFor='rotas-assignmentNotes'>Notes</label>
                    <input
                      id='rotas-assignmentNotes'
                      type='text'
                      value={assignmentModalNotes}
                      onChange={(event) => setAssignmentModalNotes(event.target.value)}
                      placeholder='Optional notes for this rota slot'
                    />
                  </div>

                  <div className='sysManager_rotas_modalActions'>
                    <button
                      className='sysManager_rotas_secondaryButton'
                      type='button'
                      onClick={closeAssignmentModal}
                    >
                      Cancel
                    </button>
                    <button
                      className='sysManager_rotas_primaryButton'
                      type='button'
                      onClick={handleAssignmentModalSave}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </BookingQuestionsModal>
          </section>
        )}
      </div>
    </div>
  );
}
